import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../AuthContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fileIcon(name = '') {
  const ext = name.split('.').pop().toLowerCase();
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (['xls', 'xlsx'].includes(ext)) return '📊';
  if (['ppt', 'pptx'].includes(ext)) return '📑';
  if (ext === 'pdf') return '📄';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return '🖼️';
  return '📎';
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(val) {
  if (!val) return '—';
  try {
    const d = typeof val.toDate === 'function' ? val.toDate() : new Date(val);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

export default function StudentTaskView({ task, allSubmissions = [], onBack }) {
  const { currentUser, userProfile } = useAuth();

  // ── Data state
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Upload state
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fileInputRef = useRef();

  // Derive submission from parent-provided data
  useEffect(() => {
    if (!task?.id || !currentUser?.uid) return;
    const sub = allSubmissions.find(s => s.taskId === task.id && s.studentId === currentUser.uid);
    setSubmission(sub || null);
    setLoading(false);
  }, [task?.id, currentUser?.uid, allSubmissions]);

  // ── File actions
  const handleFilePick = (e) => {
    const files = Array.from(e.target.files);
    const tooBig = files.filter((f) => f.size > 25 * 1024 * 1024);
    if (tooBig.length) {
      setError(`File exceeds 25 MB limit: ${tooBig.map((f) => f.name).join(', ')}`);
      return;
    }
    setPendingFiles((prev) => [...prev, ...files]);
    setError('');
    e.target.value = '';
  };

  const removePendingFile = (idx) =>
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  const uploadFiles = async (subId, files) => {
    const results = [];
    for (const file of files) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `task-submissions/${task.id}/${currentUser.uid}/${Date.now()}_${safeName}`;
      const sRef = storageRef(storage, path);
      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(sRef, file);
        uploadTask.on(
          'state_changed',
          (snap) => {
            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
            setUploadProgress((prev) => ({ ...prev, [file.name]: pct }));
          },
          reject,
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            results.push({ name: file.name, url, path, type: file.type, size: file.size });
            resolve();
          }
        );
      });
    }
    return results;
  };

  const handleSubmit = async () => {
    if (pendingFiles.length === 0) {
      setError('Please attach at least one file to submit.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const subId = `${task.id}_${currentUser.uid}`;
      const newAttachments = await uploadFiles(subId, pendingFiles);
      await setDoc(doc(db, 'taskSubmissions', subId), {
        taskId: task.id,
        taskTitle: task.title,
        teacherId: task.teacherId,
        studentId: currentUser.uid,
        studentName: userProfile?.name || 'Student',
        studentEmail: currentUser.email,
        attachments: newAttachments,
        submittedAt: serverTimestamp(),
        status: 'submitted',
      });
      setPendingFiles([]);
      setUploadProgress({});
    } catch (err) {
      console.error('Submission error:', err);
      setError('Failed to submit. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="loading-content">Loading task…</div>;

  const isDue = task.dueDate && new Date(task.dueDate) < new Date();

  return (
    <div className="td-wrapper animate-in">

      {/* ── Header ── */}
      <div className="td-header">
        <button className="btn-link" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '14px' }} onClick={onBack}>
          ← Back to Curriculum
        </button>
        <div className="td-header-status">
          {submission ? (
            <span className="status-badge status-badge--submitted">✓ Completed</span>
          ) : (
            <span className={`status-badge ${isDue ? 'status-badge--overdue' : 'status-badge--pending'}`}>
              {isDue ? '⏳ Overdue' : '🔔 Open'}
            </span>
          )}
        </div>
      </div>

      {/* ── Task Info Card ── */}
      <div className="td-info-card card-premium">
        <div className="card-top-line"></div>
        <div className="td-badges">
          <span className="chip chip--sm chip--outline">{task.subjectName}</span>
          <span className="chip chip--sm chip--outline">📖 {task.chapterName}</span>
          {task.dueDate && (
            <span className={`chip chip--sm ${!submission && isDue ? 'chip--danger' : 'chip--outline'}`}>
              📅 Due {formatDate(task.dueDate)}
            </span>
          )}
        </div>
        <h2 className="td-task-title">{task.title}</h2>
        {task.description && <p className="td-description">{task.description}</p>}
      </div>

      {/* ── Teacher Attachments ── */}
      {task.attachments?.length > 0 && (
        <div className="td-section">
          <h4 className="td-section-title">📎 Study Materials</h4>
          <div className="td-attachments-grid">
            {task.attachments.map((att, i) => (
              <a key={i} href={att.url} target="_blank" rel="noreferrer" className="td-attach-chip">
                <span className="attach-icon">{fileIcon(att.name)}</span>
                <span className="attach-name">{att.name}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* ── Submission Section ── */}
      <div className="td-section">
        <h4 className="td-section-title">
          {submission ? '📊 Your Submission' : '📤 Submit Your Work'}
        </h4>

        {submission ? (
          /* Already submitted */
          <div className="submission-row submission-row--done animate-in">
            <div className="submission-info">
              <span className="submission-time" style={{ display: 'block', marginBottom: 14 }}>
                Submitted on {formatDate(submission.submittedAt)} 🎉
              </span>
              <div className="td-attachments-grid">
                {submission.attachments?.map((att, i) => (
                  <a key={i} href={att.url} target="_blank" rel="noreferrer" className="td-attach-chip">
                    <span className="attach-icon">{fileIcon(att.name)}</span>
                    <span className="attach-name">{att.name}</span>
                  </a>
                ))}
              </div>
            </div>
            <div className="submission-success-icon">✅</div>
          </div>
        ) : (
          /* Upload form */
          <>
            <p className="setup-label">Once you've finished your task, upload your worksheet here.</p>

            <div className="attach-list" style={{ marginBottom: 20 }}>
              {pendingFiles.length === 0 ? (
                <div className="attach-empty">
                  <span style={{ fontSize: 36 }}>📤</span>
                  <p>No files selected yet</p>
                </div>
              ) : (
                pendingFiles.map((f, i) => (
                  <div key={i} className="attach-item attach-item--pending">
                    <span className="attach-icon">{fileIcon(f.name)}</span>
                    <div className="attach-info">
                      <span className="attach-name">
                        {f.name} <em className="attach-size">({formatBytes(f.size)})</em>
                      </span>
                      {uploadProgress[f.name] !== undefined && (
                        <div className="upload-bar">
                          <div className="upload-bar-fill" style={{ width: `${uploadProgress[f.name]}%` }} />
                        </div>
                      )}
                    </div>
                    {uploadProgress[f.name] === undefined && (
                      <button className="icon-btn icon-btn--danger" onClick={() => removePendingFile(i)} disabled={submitting}>×</button>
                    )}
                  </div>
                ))
              )}
            </div>

            {error && <p className="form-error" style={{ marginBottom: 16 }}>{error}</p>}

            <div className="submission-actions">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFilePick}
                disabled={submitting}
                accept=".doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.png,.jpg,.jpeg,.txt,.csv,.zip"
              />
              <button
                className="btn-outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
              >
                + Choose Files
              </button>
              <button
                className="btn-primary"
                style={{ width: 'auto', marginTop: 0, marginLeft: 'auto', padding: '11px 24px' }}
                onClick={handleSubmit}
                disabled={submitting || pendingFiles.length === 0}
              >
                {submitting ? 'Submitting…' : 'Submit & Complete Task'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
